import { removeAllCookies } from "../storage/cookieManager";
import { AUTH_BASE_URL, ERP_BASE_URL } from "../../services/api/helper/environment/EnvironmentConfig";

const getFileNameFromUrl = (url) => {
    if (!url) return "";
    const fileName = url.split("/").pop()?.split("?")[0];
    return decodeURIComponent(fileName);
};

export const getFileObject = (url) => {
    if (!url) return "";
    return { file: { name: getFileNameFromUrl(url) }, base64Data: url };
};

// Reusable utility
export const triggerDownload = (blob, filename) => {
    if (!blob || !filename) return;
    if (blob instanceof Blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url); // cleanup memory
    }
};

export const getYear = (dateStr) => {
    if (!dateStr) return "";
    const parsed = typeof dateStr === "number" ? String(dateStr) : dateStr;
    const dateObj = new Date(parsed);
    const year = dateObj.getFullYear();
    return isNaN(year) ? "" : String(year);
};

const handleERPRedirectPortal = () => {
    removeAllCookies();
    localStorage.clear();
    return ERP_BASE_URL;
}

export const redirectToPortal = (isErpLoggedIn, preFix = "") => {
    return isErpLoggedIn ? handleERPRedirectPortal() : `${AUTH_BASE_URL}${preFix}`;
};

export const resolveDropdownValue = (value, data, valueField = 'id', labelField = 'value') => {
    if (value == null || value === '') return value;
    const id = value && typeof value === 'object' ? (value.value ?? value[valueField]) : value;
    let match = data?.find((item) => String(item[valueField]) === String(id));
    if (!match && labelField) {
        match = data?.find((item) => String(item[labelField]).toLowerCase() === String(id).toLowerCase());
    }
    if (!match) return value;
    // Return the SAME reference when value is already the correctly resolved object — this is
    // called fresh on every buildConfig/render pass, and constructing a new {value, label} object
    // even when nothing changed gives configInitialState a new reference every time, which makes
    // FormBuilder's reset effect replace its entire internal formData on every keystroke anywhere
    // in the form (not just this field), churning every dropdown's rendered value unnecessarily.
    if (
        value && typeof value === 'object' &&
        String(value.value) === String(match[valueField]) &&
        String(value.label) === String(match[labelField])
    ) {
        return value;
    }
    return { value: match[valueField], label: match[labelField] };
};

export const getFirstNavigationPath = (config) => {
    if (!config || !Array.isArray(config)) return null;
    for (const element of config) {
        if (element.navigationPath) {
            return element.navigationPath;
        }
        if (element.items && Array.isArray(element.items)) {
            const path = getFirstNavigationPath(element.items);
            if (path) return path;
        }
        if (element.children && Array.isArray(element.children)) {
            const path = getFirstNavigationPath(element.children);
            if (path) return path;
        }
    }
    return null;
};

export const getAllNavigationPaths = (config) => {
    if (!config || !Array.isArray(config)) return [];
    let paths = [];
    for (const element of config) {
        if (element.navigationPath) {
            paths.push(element.navigationPath);
        }
        if (element.items && Array.isArray(element.items)) {
            paths = paths.concat(getAllNavigationPaths(element.items));
        }
        if (element.children && Array.isArray(element.children)) {
            paths = paths.concat(getAllNavigationPaths(element.children));
        }
    }
    return paths;
};

export const isNavigationPathAllowed = (pathname, config) => {
    if (
        !pathname ||
        pathname === "/" ||
        pathname === "/profile" ||
        // Own-profile sub pages (e.g. a company editing its profile at /profile/edit-company)
        pathname.startsWith("/profile/") ||
        pathname === "/edit-profile" ||
        pathname === "/settings" ||
        pathname === "/view-student-notification"
    ) return true;
    if (!config || !Array.isArray(config) || config.length === 0) return true;

    const allowedPaths = getAllNavigationPaths(config);
    if (allowedPaths.length === 0) return true;

    const cleanPathname = pathname.endsWith("/") && pathname.length > 1 ? pathname.slice(0, -1) : pathname;

    return allowedPaths.some((allowed) => {
        const cleanAllowed = allowed.endsWith("/") && allowed.length > 1 ? allowed.slice(0, -1) : allowed;
        return cleanPathname === cleanAllowed || cleanPathname.startsWith(cleanAllowed + "/");
    });
};

/**
 * Builds the display name shown across portals: First Name + Surname (middle name is never shown).
 * Falls back to the first and last word of fullName when the name parts aren't available.
 *
 * @param {string} firstName - First name
 * @param {string} lastName - Surname
 * @param {string} fullName - Full name fallback
 * @returns {string} Display name (e.g., "Parmar Mahendrabhai" for Parmar Shruten Mahendrabhai)
 */
export const getDisplayName = (firstName = "", lastName = "", fullName = "") => {
    const cleanFn = String(firstName || "").trim();
    const cleanLn = String(lastName || "").trim();

    if (cleanFn || cleanLn) {
        return [cleanLn, cleanFn].filter(Boolean).join(" ");
    }

    const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
    return parts.length > 2 ? `${parts[0]} ${parts.slice(1, -1).join(" ")}` : parts.join(" ");
};

/**
 * Splits a full name into its first, middle and last parts, in the order they appear.
 * e.g. "Kamalekar Jagdish Tulshiram" -> { firstName: "Kamalekar", middleName: "Jagdish", lastName: "Tulshiram" }
 *
 * @param {string} fullName - Full name as received (cookie / API)
 * @returns {{ firstName: string, middleName: string, lastName: string }}
 */
export const splitFullName = (fullName = "") => {
    const [firstName = "", middleName = "", ...rest] = String(fullName || "").trim().split(/\s+/).filter(Boolean);
    return { firstName, middleName, lastName: rest.join(" ") };
};

/**
 * Display name built from the full name's first and middle parts
 * (e.g. "Kamalekar Jagdish Tulshiram" -> "Kamalekar Jagdish").
 * Falls back to getDisplayName(firstName, lastName) when no full name is available.
 *
 * @param {string} fullName - Full name as received (cookie / API)
 * @param {string} firstName - Fallback first name
 * @param {string} lastName - Fallback last name
 * @returns {string}
 */
export const getFirstMiddleName = (fullName = "", firstName = "", lastName = "") => {
    const { firstName: first, middleName } = splitFullName(fullName);
    if (first) return [first, middleName].filter(Boolean).join(" ");
    return getDisplayName(firstName, lastName);
};

/**
 * Generates 1 or 2 uppercase initials for a user's name following standard naming conventions
 * (First Name Initial + Last Name Initial).
 *
 * @param {string} firstName - First name (or given name)
 * @param {string} lastName - Last name (or surname)
 * @param {string} fullName - Full name fallback
 * @returns {string} Initials string (e.g., "VC" for Vidit Singh Chauhan)
 */
export const getUserInitials = (firstName = "", lastName = "", fullName = "") => {
    const cleanFn = String(firstName || "").trim();
    const cleanLn = String(lastName || "").trim();
    const cleanFull = String(fullName || "").trim();

    if (cleanFn && cleanLn) {
        const firstInitial = cleanFn.split(/\s+/)[0]?.[0] || "";
        const lastInitial = cleanLn.split(/\s+/).pop()?.[0] || "";
        return (firstInitial + lastInitial).toUpperCase();
    }

    const singleName = cleanFn || cleanLn || cleanFull;
    if (singleName) {
        const parts = singleName.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return parts[0]?.[0]?.toUpperCase() || "";
    }

    return "";
};

/**
 * Transforms flat semesterYear API data into grouped dropdown options (Semester / Year)
 * and sets them on the specified form field using setGroupedDropDownOptionField.
 * Uses composite IDs (e.g., "sem_1", "year_1") to avoid collisions between
 * semesters and years that share the same numeric ID.
 *
 * @param {Array} semesterYearContent - The semesterYear content array from the API response.
 * @param {object} formConfig - The form configuration object containing the field.
 * @param {string} fieldId - The field ID to set options on (e.g., "semesterYearId").
 */
export const setSemesterYearGroupedOptions = async (semesterYearContent, formConfig, fieldId) => {
    if (semesterYearContent && semesterYearContent.length > 0) {
        const transformedData = [
            { uniqueId: "semester", value: "Semester" },
            { uniqueId: "year", value: "Year" },
            ...semesterYearContent.map(item => ({
                ...item,
                uniqueId: item.admissionSystem ? `sem_${item.id}` : `year_${item.id}`,
                groupKey: item.admissionSystem ? "semester" : "year"
            }))
        ];
        const { setGroupedDropDownOptionField } = await import("@repo/design-system/components/forms/utils");
        setGroupedDropDownOptionField(transformedData, "uniqueId", "value", "groupKey", formConfig, fieldId, null, ["admissionSystem", "id"]);
    }
};

/**
 * Parses the selected semesterYear composite value back into the original ID and admissionSystem flag.
 *
 * @param {object} selectedOption - The selected option object from the dropdown (e.g., { value: "sem_1", admissionSystem: true, id: 1 }).
 * @returns {{ semId: number|string|null, admissionSystem: boolean|null }}
 */
export const parseSemesterYearValue = (selectedOption) => {
    if (!selectedOption || typeof selectedOption !== "object") return { semId: null, admissionSystem: null };
    return {
        semId: selectedOption.id ?? null,
        admissionSystem: selectedOption.admissionSystem ?? null
    };
};


export const handlePortalSelect = (portal, navigate, isErpLoggedIn) => {
    if (!portal) return;

    if (!portal.enabled) {
        window.location.href = redirectToPortal(isErpLoggedIn, `/coming-soon?portal=${portal.key}`);
        // navigate(portal.path || `/coming-soon?portal=${portal.key}`);
        return;
    }

    if (portal.comingSoon || portal.path?.startsWith('/coming-soon') || portal.path?.startsWith('/')) {
        window.location.href = redirectToPortal(isErpLoggedIn, `/coming-soon?portal=${portal.key}`);
        // navigate(portal.path);
        return;
    }

    // Store selected portal in localStorage
    localStorage.setItem('selectedPortal', portal.key);
    localStorage.setItem('lastAccessedPortal', portal.path);

    // Navigate to the selected app
    const isDevelopment = import.meta.env.DEV;

    if (isDevelopment) {
        // In development: open in new window (different ports)
        window.open(portal.path, '_self');
    } else {
        // In production: use window.location (same domain)
        window.location.href = portal.path;
    }
};